/**
 * @fileoverview Quota Exceeded Create Modal component.
 * @summary Modal for blocking event creation when quota is exceeded, with CTA to upgrade to ONE_SHOT or plan.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/QuotaExceededCreateModal
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faBolt, faExclamationTriangle, faInfoCircle, faArrowUp } from '@fortawesome/free-solid-svg-icons'
import { SUBSCRIPTION_PLANS, SubscriptionType } from '@karaonow/shared'
import Spinner from '../../../components/Spinner'
import type { FC } from 'react'

interface QuotaExceededCreateModalProps {
  isOpen: boolean
  isLoading?: boolean
  currentCount?: number
  limit?: number
  userPlan?: string
  onCreateOneShot?: () => void
  onCancel: () => void
}

/**
 * @summary Modal for blocking event creation when quota is exceeded.
 * @param {QuotaExceededCreateModalProps} props - Component props
 * @returns {ReactElement | null} Quota exceeded modal or null if not open
 */
const QuotaExceededCreateModal: FC<QuotaExceededCreateModalProps> = ({
  isOpen,
  isLoading = false,
  currentCount,
  limit,
  userPlan,
  onCreateOneShot,
  onCancel,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  if (!isOpen) return null

  const oneShotPlan = SUBSCRIPTION_PLANS[SubscriptionType.ONE_SHOT]
  const planType = (userPlan as SubscriptionType) || SubscriptionType.FREE
  const planInfo = SUBSCRIPTION_PLANS[planType]
  const isFreePlan = planType === SubscriptionType.FREE

  // Determine if user can upgrade to higher plan
  const canUpgradePlan = planType !== SubscriptionType.ULTIMATE

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-2 pt-16 bg-black/20">
      <div className="bg-(--color-bg-primary) rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 sm:p-6 shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-100 dark:bg-red-900/30 shrink-0">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              {isFreePlan 
                ? (t('events.quotaExceededCreateTitleFree') || 'Quota atteint')
                : (t('events.quotaExceededCreateTitle') || 'Quota mensuel atteint')}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
            aria-label={t('common.close')}
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
          {/* Error Message */}
          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-900 dark:text-red-200">
              {isFreePlan
                ? (t('events.quotaExceededCreateMessageFree', { planName: planInfo?.displayName || 'Plan' }) || 
                    `Vous avez atteint la limite de votre plan. Vous ne pouvez pas créer plus d'événements.`)
                : (t('events.quotaExceededCreateMessage', { planName: planInfo?.displayName || 'Plan' }) || 
                    `Vous avez atteint la limite de votre plan. Vous ne pouvez pas créer plus d'événements pour cette période.`)}
            </p>
            {currentCount !== undefined && limit !== undefined && (
              <p className="text-xs text-red-700 dark:text-red-300 mt-2">
                {isFreePlan
                  ? (t('events.quotaExceededDetailsFree', { current: currentCount, limit }) || 
                      `Vous avez ${currentCount} événement(s) sur ${limit}.`)
                  : (t('events.quotaExceededDetails', { current: currentCount, limit }) || 
                      `Vous avez ${currentCount} événement(s) sur ${limit} pour cette période.`)}
              </p>
            )}
          </div>

          {/* Explanation */}
          <div>
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              {isFreePlan
                ? (t('events.quotaExceededCreateExplanationFree') || 
                    'Vous avez atteint la limite d\'événements de votre plan gratuit. Vous pouvez passer à un plan supérieur pour créer plus d\'événements.')
                : (t('events.quotaExceededCreateExplanation') || 
                    'Votre quota mensuel d\'événements est atteint. Vous pouvez soit attendre la fin de votre période de facturation, soit passer à un plan supérieur.')}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isFreePlan
                ? (t('events.quotaExceededCreateOptionsFree') || 
                    'Vous pouvez également créer un événement unique (ONE_SHOT) qui ne comptera pas dans votre quota.')
                : (t('events.quotaExceededCreateOptions') || 
                    'Vous pouvez également créer un événement unique (ONE_SHOT) qui ne comptera pas dans votre quota mensuel.')}
            </p>
          </div>

          {/* ONE_SHOT Option */}
          {onCreateOneShot && (
            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <div className="flex items-start gap-2 mb-3">
                <FontAwesomeIcon icon={faBolt} className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 mb-1">
                    {t('events.createOneShotOption') || 'Option : Créer un Événement Unique'}
                  </h3>
                  <p className="text-xs text-indigo-700 dark:text-indigo-300 mb-2">
                    {isFreePlan
                      ? (t('events.createOneShotDescriptionFree') || 
                          'Créez un événement unique qui ne comptera pas dans votre quota.')
                      : (t('events.createOneShotDescription') || 
                          'Créez un événement unique qui ne comptera pas dans votre quota mensuel.')}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('subscription.price') || 'Prix'}
                    </span>
                    <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                      €{oneShotPlan.priceEur?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upgrade Plan Option */}
          {canUpgradePlan && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <FontAwesomeIcon icon={faArrowUp} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                    {t('events.upgradePlanOption') || 'Option : Passer au plan supérieur'}
                  </h3>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                    {isFreePlan
                      ? (t('events.upgradePlanDescriptionFree') || 
                          'Passez à un plan supérieur pour augmenter votre quota d\'événements.')
                      : (t('events.upgradePlanDescription') || 
                          'Passez à un plan supérieur pour augmenter votre quota mensuel d\'événements.')}
                  </p>
                  <Link
                    to="/subscription"
                    className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                  >
                    {t('subscription.comparePlans') || 'Comparer les plans →'}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Pro/Premium reset quota info */}
          {planType === SubscriptionType.PRO || planType === SubscriptionType.PREMIUM && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-2">
              <FontAwesomeIcon icon={faInfoCircle} className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-amber-900 dark:text-amber-200">
                  {t('events.quotaResetInfo') || 
                    'Votre quota sera réinitialisé à la fin de votre période de facturation.'}
                </p>
              </div>
            </div>
          </div> )}
        </div>
       

        {/* Footer - Sticky */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 shrink-0 rounded-b-lg bg-(--color-bg-primary)">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="btn btn-secondary order-2 sm:order-1"
          >
            {t('common.cancel') || 'Annuler'}
          </button>
          {onCreateOneShot && (
            <button
              type="button"
              onClick={onCreateOneShot}
              disabled={isLoading}
              className="btn btn-primary flex items-center justify-center gap-2 order-1 sm:order-2"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" variant="white" />
                  <span>{t('common.processing') || 'Traitement'}...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faBolt} className="w-4 h-4" />
                  <span>{t('events.createOneShotNow') || '🚀 Créer un Événement Unique'}</span>
                </>
              )}
            </button>
          )}
          {canUpgradePlan && !onCreateOneShot && (
            <button
              type="button"
              onClick={() => {
                navigate('/subscription')
                onCancel()
              }}
              disabled={isLoading}
              className="btn btn-primary flex items-center justify-center gap-2 order-1 sm:order-2"
            >
              <FontAwesomeIcon icon={faArrowUp} className="w-4 h-4" />
              <span>{t('events.upgradePlanNow') || 'Passer au plan supérieur'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuotaExceededCreateModal
